<script setup lang="ts">
import { ref, onMounted, nextTick, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useTranslation } from '@/utils/locales/messages';
import { useContactStore } from '../stores/contacts.store';
import { contactsApi } from '../api/contactsApi';
import { useDialog } from '@/shared/composables/useDialog';
import { usePermissionGuard } from '@/shared/composables/usePermissionGuard';
import BasicIndividualSection from '../components/BasicIndividualSection.vue';
import ContactAddressForm from '../components/ContactAddressForm.vue';
import ContactInfoForm from '../components/ContactInfoForm.vue';
import type { CreateIndividualData, CreateIndividualPayload } from '../types/contact.types';
import { buildSaveEmailFields } from '../utils/buildSaveEmailFields';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { isMyPassportIdType } from '../utils/myPassportIdType';

const router = useRouter();
const sessionStore = useSessionStore();
const contactStore = useContactStore();
const { alert } = useDialog();
const { createGuardedAction } = usePermissionGuard();

const { createSyncT } = useTranslation();
const currentLanguage = ref(localStorage.getItem('preferred-language') || 'en');
const t = createSyncT(currentLanguage);

// 모달 임베드 모드 — true 면 저장 시 라우팅 대신 created emit (PreSet 등 공통 ContactRegisterModal 에서 사용).
//   기본 false = 기존 페이지 동작 그대로(라우팅). [[my_register_contact_address_3tables]]
const props = withDefaults(defineProps<{
    isModal?: boolean;
    /** 초기값(선택) — 주면 formData 에 깊은 병합(S2000 Convert 등). 없으면 기존과 동일 */
    initialData?: Partial<CreateIndividualData> | null;
    /** true 면 이메일 필수 검사 생략(S2000 Convert 의 Contact 추가 전용). 기본 false = 기존처럼 필수 */
    emailOptional?: boolean;
}>(), { isModal: false, initialData: null, emailOptional: false });
const emit = defineEmits<{
    (e: 'created', payload: { contactCd: string; data: CreateIndividualPayload }): void;
    (e: 'cancel'): void;
}>();

// 타이틀 번역을 computed로 처리
const pageTitle = computed(() => {
    const translated = t('contact.Registrable Individual');
    // 번역이 키를 반환하면 fallback 사용
    if (translated && translated !== 'contact.Registrable Individual') {
        return translated;
    }
    return 'Registrable Individual';
});

onMounted(() => {
    window.addEventListener('language-changed', (event: any) => {
        currentLanguage.value = event.detail.language;
    });
});

const formData = ref<CreateIndividualData>({
    identificationType: '',
    identificationNo: '',
    nationality: '',
    dateOfBirth: '',
    passportNoExpiryDate: '',
    contactName: '',
    firstName: '',
    middleName: '',
    surname: '',
    profileGender: '',
    profileRace: '',
    profileOccupation: '',
    profileSecretaryLicenseNo: '',
    profileSecretaryCertificateNo: '',
    profileOtherDirectorship: '',
    profileFirmNo: '',
    profileIssuingCountry: '',
    profilePlaceOfBirth: '',
    addressType: 'B07001',
    localAddress: {
        postalCode: '',
        blockHouseNo: '',
        streetName: '',
        buildingEstateName: '',
        level: '',
        unit: '',
    },
    foreignAddress: {
        addressLine1: '',
        addressLine2: '',
    },
    contactInfo: {
        countryCode: '',
        areaCode: '',
        mobileNos: [''],
        emails: [''],
        note: '',
    },
});

// 초기값 깊은 병합 — setup 시점(하위 폼이 마운트 전에 값을 보도록). initialData 가 없으면 아무것도 안 함
if (props.initialData && typeof props.initialData === 'object') {
    const init = props.initialData as Record<string, any>;
    const base = formData.value as Record<string, any>;
    for (const [key, value] of Object.entries(init)) {
        if (value === undefined || value === null) continue;
        if (typeof value === 'object' && !Array.isArray(value) && base[key] && typeof base[key] === 'object') {
            base[key] = { ...base[key], ...value };
        } else {
            base[key] = value;
        }
    }
    // MY 주소 폼은 localAddress 가 "바뀔 때만" 편집 칸(주소 줄·국가·주·도시)을 채운다 →
    // 마운트 직후 같은 값을 새 객체로 한 번 더 넣어 편집 칸까지 반영되게 한다.
    if (init.localAddress && typeof init.localAddress === 'object') {
        const initLocal = { ...(init.localAddress as Record<string, unknown>) };
        onMounted(() => {
            setTimeout(() => {
                (formData.value as Record<string, any>).localAddress = {
                    ...((formData.value as Record<string, any>).localAddress || {}),
                    ...initLocal,
                };
            }, 300);
        });
    }
}

const isSubmitting = ref(false);
const contactAddressFormRef = ref<InstanceType<typeof ContactAddressForm> | null>(null);
const contactInfoFormRef = ref<InstanceType<typeof ContactInfoForm> | null>(null);

const isJurisdictionMY = computed(() => {
    const jurisdiction = sessionStore.user?.customer?.jurisdiction || '';
    const j = jurisdiction.toUpperCase();
    return j === 'MY' || j === 'MALAYSIA';
});

const handleSave = async () => {
    if (isSubmitting.value) return;

    const addressModel =
        contactAddressFormRef.value?.getAddressModelForSave?.() ?? formData.value;
    const contactInfoPayload =
        contactInfoFormRef.value?.getContactInfoPayloadForSave?.() ??
        (formData.value.contactInfo as any);

    const email = contactInfoPayload?.emails?.[0]?.trim();
    const contactName = (formData.value as any).contactName?.trim()
        || (formData.value as any).CONTACT_NAME?.trim()
        || '';
    const firstName = formData.value.firstName?.trim();
    const surname = formData.value.surname?.trim();

    if (isJurisdictionMY.value) {
        if (!contactName) {
            await alert({
                title: t('contact.Error') || 'Error',
                message: t('contact.Name is required') || 'Name is required',
                type: 'error'
            });
            return;
        }
    } else {
        if (!firstName || firstName === '') {
            await alert({
                title: t('contact.Error') || 'Error',
                message: t('contact.First Name is required') || 'First Name is required',
                type: 'error'
            });
            return;
        }
        if (!surname || surname === '') {
            await alert({
                title: t('contact.Error') || 'Error',
                message: t('contact.Surname is required') || 'Surname is required',
                type: 'error'
            });
            return;
        }
    }
    
    if (!props.emailOptional && (!email || email === '')) {
        await alert({
            title: t('contact.Error') || 'Error',
            message: t('contact.Email Address is required') || 'Email Address is required',
            type: 'error'
        });
        return;
    }
    
    isSubmitting.value = true;
    try {
        // dateOfBirth 값 확인
        console.log('📅 [handleSave] formData dateOfBirth 값 확인:', {
            dateOfBirth: formData.value.dateOfBirth,
            formData: formData.value
        });
        
        const { emails, emailMainYn, emailSyncYn } = buildSaveEmailFields(contactInfoPayload);

        const altLocalAddress = {
            postalCode: contactInfoPayload?.ALT_POSTAL_CODE || '',
            blockHouseNo: contactInfoPayload?.ALT_BLOCK_NO || '',
            streetName: contactInfoPayload?.ALT_STREET_NAME || '',
            buildingEstateName: contactInfoPayload?.ALT_BUILDING_NAME || '',
            level: contactInfoPayload?.ALT_ADDR_LEVEL || '',
            unit: contactInfoPayload?.ALT_ADDR_UNIT || '',
        };
        const altForeignAddress = {
            addressLine1: contactInfoPayload?.ALT_FOREIGN_ADDRESS_LINE_1 || '',
            addressLine2: contactInfoPayload?.ALT_FOREIGN_ADDRESS_LINE_2 || '',
        };

        const addressType: CreateIndividualPayload['addressType'] =
            addressModel.addressType === 'B07002' || formData.value.addressType === 'B07002'
                ? 'B07002'
                : 'B07001';

        const isMyPassport = isJurisdictionMY.value && isMyPassportIdType(formData.value as Record<string, unknown>);

        const createData: CreateIndividualPayload = {
            identificationType: formData.value.identificationType || '',
            identificationNo: formData.value.identificationNo || '',
            nationality: formData.value.nationality || '',
            dateOfBirth: formData.value.dateOfBirth || '',
            passportNoExpiryDate: (isMyPassport || !isJurisdictionMY.value) ? (formData.value.passportNoExpiryDate || '') : '',
            firstName: isJurisdictionMY.value ? contactName : firstName,
            middleName: isJurisdictionMY.value ? '' : (formData.value.middleName || ''),
            surname: isJurisdictionMY.value ? '' : (surname || ''),
            contactName: isJurisdictionMY.value ? contactName : undefined,
            profileGender: formData.value.profileGender || '',
            profileRace: formData.value.profileRace || '',
            profileOccupation: formData.value.profileOccupation || '',
            profileSecretaryLicenseNo: formData.value.profileSecretaryLicenseNo || '',
            profileSecretaryCertificateNo: formData.value.profileSecretaryCertificateNo || '',
            profileOtherDirectorship: formData.value.profileOtherDirectorship || '',
            profileFirmNo: formData.value.profileFirmNo || '',
            profileIssuingCountry: isMyPassport ? (formData.value.profileIssuingCountry || '') : '',
            profilePlaceOfBirth: formData.value.profilePlaceOfBirth || '',
            addressType,
            localAddress: addressModel.localAddress || formData.value.localAddress || {},
            foreignAddress: addressModel.foreignAddress || formData.value.foreignAddress || {},
            contactInfo: {
                countryCode: contactInfoPayload?.countryCode || '',
                areaCode: contactInfoPayload?.areaCode || '',
                mobileNos: contactInfoPayload?.mobileNos || [''],
                emails: emails.length > 0 ? emails : [''],
                emailMainYn,
                emailSyncYn,
                note: contactInfoPayload?.note || '',
                ...(isJurisdictionMY.value
                    ? {}
                    : {
                        altLocalAddress,
                        altForeignAddress,
                        serviceAddressList: Array.isArray(contactInfoPayload?.serviceAddressList)
                            ? contactInfoPayload.serviceAddressList
                            : [],
                        businessAddressList: Array.isArray(contactInfoPayload?.businessAddressList)
                            ? contactInfoPayload.businessAddressList
                            : [],
                    }),
            },
        };

        const response = await contactsApi.createIndividual(createData);
        const responseData = (response as any).data || response;
        const contactCd = responseData?.contactCd;

        // 모달 모드 — 라우팅/성공 알럿 대신 created emit (호출측이 닫고 행을 채움).
        if (props.isModal) {
            emit('created', { contactCd: String(contactCd || ''), data: createData });
            return;
        }

        // 성공 메시지 표시
        const successMessage = responseData?.message || t('contact.Contact created successfully') || 'Contact created successfully';
        await alert({
            title: t('contact.Success') || 'Success',
            message: successMessage,
            type: 'success'
        });

        // Contact 상세 페이지로 이동
        if (contactCd) {
            router.push(`/Contact/${contactCd}`);
        } else {
            // 리스트로 돌아갈 때 데이터 새로고침 이벤트 발생
            window.dispatchEvent(new CustomEvent('contact-list-refresh'));
            router.push('/Contact');
        }
    } catch (error: any) {
        // 에러 처리
        const responseData = error?.response?.data;
        
        let errorMessage = '';
        if (responseData?.error) {
            if (typeof responseData.error === 'object' && responseData.error.message) {
                errorMessage = responseData.error.message;
            } else if (typeof responseData.error === 'string') {
                errorMessage = responseData.error;
            }
        }
        
        // 백엔드에서 반환한 다국어 메시지만 사용
        if (!errorMessage) {
            errorMessage = responseData?.message || error?.message || '';
        }
        
        await alert({
            title: t('contact.Error') || 'Error',
            message: errorMessage || t('contact.Failed to create contact') || 'Failed to create contact',
            type: 'error'
        });
    } finally {
        isSubmitting.value = false;
    }
};

// Guarded Save click: prevent confirm/side effects when permission is denied
const onSaveClickGuard = createGuardedAction('/Contact', 'Save', () => handleSave());

const handleCancel = () => {
    if (props.isModal) { emit('cancel'); return; }
    router.push('/Contact');
};
</script>

<template>
    <v-container fluid :class="isModal ? 'pa-0' : 'page-wrapper'">
        <v-row>
            <v-col cols="12" :class="isModal ? 'pa-0' : ''">
                <v-card :flat="isModal" :class="isModal ? '' : 'contact-create-card'">
                    <v-card-title v-if="!isModal" class="contact-card-title">
                        {{ pageTitle }}
                    </v-card-title>
                    <v-card-text>
                        <BasicIndividualSection v-model="formData" :hide-kyc-status="true" />
                        <ContactAddressForm
                            ref="contactAddressFormRef"
                            v-model="formData"
                            suppress-parent-emit
                            :is-individual="true"
                            :simple-address-label="isJurisdictionMY"
                            :hide-address-consolidation-ui="isJurisdictionMY"
                        />
                        <ContactInfoForm
                            ref="contactInfoFormRef"
                            v-model="formData.contactInfo"
                            suppress-parent-emit
                            :require-email="!emailOptional"
                            :show-sync-badge="false"
                            :manual-override="true"
                            :is-individual="true"
                            :address-type="formData.addressType"
                            :hide-address-sections="isJurisdictionMY"
                            :simple-address-label="isJurisdictionMY"
                        />
                        
                        <div class="d-flex justify-end mt-6">
                            <v-btn
                                v-permission-button
                                data-menu-key="/Contact"
                                data-button-name="Save"
                                color="primary"
                                variant="outlined"
                                class="mr-2"
                                @click="handleCancel"
                            >
                                {{ t('contact.Cancel') || 'Cancel' }}
                            </v-btn>
                            <v-btn
                                color="primary"
                                variant="elevated"
                                :loading="isSubmitting"
                                @click="onSaveClickGuard"
                            >
                                {{ t('contact.SAVE') || 'SAVE' }}
                            </v-btn>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<style scoped lang="scss">
.page-wrapper {
    padding: 24px;
}

.contact-create-card {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.contact-card-title {
    font-size: 21px;
    font-weight: 600;
    color: #3C4650;
    padding: 16px 24px;
    border-bottom: 1px solid #ebf1f6;
}
</style>

